import { useQuery } from '@apollo/client';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';
import { GET_PROCESS_AREA_SUMMARY_DASHBOARD } from '../queries';

import TableV3 from '../../../components/generic/Table/TableV3';
import { Button } from '../../../components';
import { tableSortingMethods } from '../../../utils';
import { ColumnsType } from 'antd/es/table';
import FinancialData from '../../../components/common/FinancialData';
import { findParent } from '../../../utils/findParent';
import QueryErrorBoundary from '../../../components/error-handling';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../components/common/TitleUpdater';

const ProcessAreasSummary = () => {
  const navigate = useNavigate();
  const params = useParams();

  let [searchParams] = useSearchParams();

  const { data, loading, error } = useQuery(
    GET_PROCESS_AREA_SUMMARY_DASHBOARD,
    {
      variables: { uuid: params.locationId },
    },
  );

  const locationName = searchParams.get('locationName') ?? '';

  const columns: ColumnsType<any> = [
    {
      title: 'Process Area/Process',
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      width: 200,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Revenue Supported Annually',
      dataIndex: 'revenueSupported',
      key: 'revenueSupported',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.revenueSupported, b.revenueSupported),
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: '% Of Location Supported',
      dataIndex: 'locationPercentage',
      key: 'locationPercentage',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(a.locationPercentage, b.locationPercentage),
      render: (text: number) => (
        <span className="custom-table--column">{text.toFixed(0) + '%'}</span>
      ),
    },
    {
      title: 'Underlying Processes',
      dataIndex: 'underlyingProcesses',
      key: 'underlyingProcesses',
      align: 'right',
      width: 100,

      sorter: (a, b) =>
        tableSortingMethods.number(
          a.underlyingProcesses,
          b.underlyingProcesses,
        ),
      render: (text: string) => (
        <span className="custom-table--column">{text ?? 0}</span>
      ),
    },
  ];

  const extendActions = (record: any) => {
    const type = 'children' in record ? 'processArea' : 'process';

    const processAreaId =
      type === 'process' ? findParent(record.key, data?.findAll)?.key : null;

    return type !== 'process' ? (
      <Button
        type="primary-rounded"
        className="text-sm"
        onClick={() =>
          navigate(
            `/location-selection/${
              params.locationId
            }/process-areas-summary/${record?.key}?locationName=${searchParams.get(
              'locationName',
            )}&processArea=${record?.name}&type=${type}${
              record?.type === 'process'
                ? `&processAreaId=${processAreaId}`
                : ''
            }`,
          )
        }
      >
        Edit Dependencies
      </Button>
    ) : null;
  };

  return (
    <>
      <TitleUpdater title={`${locationName}/Process Areas Summary`} />
      <TitleBar
        title="Dependency Mapping"
        subtitle={`${locationName} // Process Area Summary`}
        brief={`Click on "Edit Dependencies" to add detail for specific processes or equipment within process areas.`}
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Process Area & Process Dependency Summary"
          columns={columns}
          data={data?.findAll}
          loading={loading}
          extendActions={extendActions}
          customStyles="custom-table--dependency-mapping nested-columns--custom"
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer
        backPath={`/location-selection/${
          params.locationId
        }?locationName=${searchParams.get('locationName')}`}
        nextPath={`/location-selection/`}
        goNextText="Back to Locations"
      />
    </>
  );
};

export default ProcessAreasSummary;
